import { lazy, Suspense, useCallback, useEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type RefObject } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { MessageScope } from '../../i18n';
import { common, rooms } from '../../i18n/messages';
import { loaders } from '../../pages/lazy';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { keyboardInset } from '../../pages/chat/streaming';
import { useSwipeToClose } from '../Sheet';
import { ASSIST_DOCKED_QUERY, useAssistShell } from './AssistProvider';

/**
 * The on-screen keyboard's height while the sheet is up (v3.32): iOS does not shrink the layout viewport for
 * the keyboard, so a sheet pinned to `bottom: 0` keeps its composer under it; the visual viewport says how
 * much is covered and the sheet lifts by that (`--kb-inset`). Zero on a desktop or with the keyboard away.
 */
function useKeyboardInset(active: boolean): number {
  const [inset, setInset] = useState(0);
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!active || !vv) {
      setInset(0);
      return;
    }
    const update = () => setInset(keyboardInset(window.innerHeight, vv));
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [active]);
  return inset;
}

/**
 * The panel's chrome in the shell (planning-app.md §3.6 *AI Assist* → *The panel*, v3.15a): a
 * desktop drawer on the right — its own column beside the page from 1440 (`ASSIST_DOCKED_QUERY`; the
 * critique's §3.17: at 1280–1439 the column left Pulse 640px of content and folded the reader's
 * columns), over the page's edge between 768 and 1439 — that leaves the page interactive (a
 * `complementary` landmark, no focus trap, Escape closes it from the provider); a full-height sheet on a phone
 * (`dialog`, the rest of the shell `inert` while it is up, the tab bar hidden; *Back* closes, and so
 * do a swipe down on the handle or the head and the *Back to <page>* row at the bottom, inside the
 * safe area — the critique's §3.17: the top-left chevron was the only way out, the far corner from a
 * right thumb). The panel's content, its resizer and every string of its own are the `chat` chunk,
 * loaded on open (§4.5: the shell carries the button and this frame only).
 */
export type AssistMode = 'docked' | 'overlay' | 'sheet';

const Panel = lazy(loaders.assist);

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The modal sheet contains Tab and Shift+Tab (the same cycle as `components/Sheet.tsx`): `aria-modal` says the
 * rest is out of reach, `inert` on the shell's columns makes it so for the pointer and the reading order, and
 * this keeps the keyboard from walking past either end of the panel.
 */
function containTab(e: KeyboardEvent<HTMLElement>, root: HTMLElement | null) {
  if (e.key !== 'Tab' || !root) return;
  const items = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);
  if (items.length === 0) {
    e.preventDefault();
    return;
  }
  const first = items[0]!;
  const last = items[items.length - 1]!;
  if (e.shiftKey && (document.activeElement === first || document.activeElement === root)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

export function AssistDrawer({ crumbs, title }: { crumbs: string; title: string }) {
  const intl = useIntl();
  const assist = useAssistShell();
  const docked = useMediaQuery(ASSIST_DOCKED_QUERY);
  const ref = useRef<HTMLElement>(null);
  const { open, phone, setOpen } = assist;
  const close = useCallback(() => setOpen(false), [setOpen]);
  const swipe = useSwipeToClose(close, open && phone);
  const kbInset = useKeyboardInset(open && phone);
  // Focus enters the panel on open (keyboard or tap alike) and returns to the button on close.
  useEffect(() => {
    if (!open) return;
    const opener = assist.openerRef.current;
    ref.current?.focus({ preventScroll: true });
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, [open, assist.openerRef]);
  if (!open) return null;
  const mode: AssistMode = phone ? 'sheet' : docked ? 'docked' : 'overlay';
  // A `complementary` landmark beside the page on desktop; a `dialog` on the phone — `aside` cannot carry that role, so the sheet is a `div`.
  const Root = phone ? 'div' : 'aside';
  return (
    <Root
      ref={ref as RefObject<HTMLDivElement>}
      onKeyDown={phone ? (e) => containTab(e, ref.current) : undefined}
      onPointerDown={swipe.onPointerDown}
      onPointerMove={swipe.onPointerMove}
      onPointerUp={swipe.onPointerUp}
      onPointerCancel={swipe.onPointerCancel}
      id="assist-panel"
      className="assist"
      role={phone ? 'dialog' : undefined}
      aria-modal={phone ? 'true' : undefined}
      aria-label={intl.formatMessage(rooms.aiAssist)}
      tabIndex={-1}
      style={{ '--assist-w': `${assist.width}px`, '--kb-inset': `${kbInset}px`, ...swipe.style } as CSSProperties}
      data-testid="assist-panel"
      data-mode={mode}
      data-view={assist.view.kind}
      data-dragging={swipe.dragging || undefined}
    >
      {phone && <span className="sheet-handle" aria-hidden="true" />}
      <Suspense
        fallback={
          <p className="muted assist-loading" role="status" aria-live="polite">
            <FormattedMessage {...common.loading} />
          </p>
        }
      >
        <MessageScope ns="chat">
          <MessageScope ns="assist">
            <Panel crumbs={crumbs} mode={mode} />
          </MessageScope>
        </MessageScope>
      </Suspense>
      {phone && (
        <div className="sheet-close-row assist-close-row">
          <button type="button" className="btn sheet-close-btn" onClick={close} data-testid="assist-close-row">
            <FormattedMessage {...rooms.assistBackTo} values={{ page: title }} />
          </button>
        </div>
      )}
    </Root>
  );
}
